import React, { useEffect, useState } from 'react';
import { MapPin, Loader2, Plus, Trash2, Building, Edit, X, Check } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface LocationsProps {
  profile: any;
  setError: (error: string | null) => void;
  setSuccess: (success: string | null) => void;
}

interface Locale {
  id?: number;
  nombre: string;
  direccion: string;
  tipo: 'PoS' | 'Centro de Distribución';
}

export const Locations: React.FC<LocationsProps> = ({ profile, setError, setSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [locales, setLocales] = useState<Locale[]>([]);
  const [showLocaleForm, setShowLocaleForm] = useState(false);
  const [editingLocaleId, setEditingLocaleId] = useState<number | null>(null);
  const [newLocale, setNewLocale] = useState<Locale>({
    nombre: '',
    direccion: '',
    tipo: 'PoS'
  });
  const [savingLocale, setSavingLocale] = useState(false);
  
  useEffect(() => {
    if (profile) {
      fetchLocales();
    }
  }, [profile]);
  
  const fetchLocales = async () => {
    try {
      setLoading(true);
      
      if (profile?.id_company) {
        const { data, error } = await supabase
          .from('locales')
          .select('*')
          .eq('id_company', profile.id_company)
          .order('nombre', { ascending: true });
        
        if (error) throw error;
        setLocales(data || []);
      }
    } catch (err) {
      console.error('Error fetching locales:', err);
      setError(err instanceof Error ? err.message : 'Error loading locales');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSaveLocale = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingLocale(true);
    setError(null);
    setSuccess(null);

    try {
      if (!profile?.id_company) throw new Error('No company associated with user');

      const localeData = {
        nombre: newLocale.nombre,
        direccion: newLocale.direccion,
        tipo: newLocale.tipo
      };

      if (editingLocaleId) {
        const { error } = await supabase
          .from('locales')
          .update(localeData)
          .eq('id', editingLocaleId)
          .eq('id_company', profile.id_company);

        if (error) throw error;
        setSuccess('Local actualizado correctamente');
      } else {
        const { error } = await supabase
          .from('locales')
          .insert({ ...localeData, id_company: profile.id_company });

        if (error) throw error;
        setSuccess('Local agregado correctamente');
      }

      setNewLocale({
        nombre: '',
        direccion: '',
        tipo: 'PoS'
      });
      setEditingLocaleId(null);
      setShowLocaleForm(false);
      await fetchLocales();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving locale:', err);
      setError(err instanceof Error ? err.message : 'Error al guardar el local');
    } finally {
      setSavingLocale(false);
    }
  };

  const handleDeleteLocale = async (id: number) => {
    try {
      if (!profile?.id_company || id === undefined) return;

      const { error } = await supabase
        .from('locales')
        .delete()
        .eq('id', id)
        .eq('id_company', profile.id_company);

      if (error) throw error;
      
      setLocales(prev => prev.filter(locale => locale.id !== id));
      setSuccess('Local eliminado correctamente');
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting locale:', err);
      setError('Error al eliminar el local');
    }
  };

  const handleEditLocale = (locale: Locale) => {
    setNewLocale({
      nombre: locale.nombre,
      direccion: locale.direccion,
      tipo: locale.tipo
    });
    setEditingLocaleId(locale.id ?? null);
    setShowLocaleForm(true);
  };
  
  return (
    <div>
      {/* Locations Section */}
      <div className="bg-white rounded-lg shadow-sm mb-6">
        <div className="p-6 border-b">
          <div className="flex items-center gap-3">
            <MapPin className="w-6 h-6 text-gray-700" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Locales</h2>
              <p className="text-sm text-gray-500">
                Administra tus puntos de servicio y centros de distribución
              </p>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          {loading && (
            <div className="flex justify-center mb-4">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          )}
          
          {locales.length > 0 ? (
            <div className="space-y-4 mb-6">
              {locales.map((locale) => (
                <div 
                  key={locale.id} 
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                >
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      {locale.tipo === 'PoS' ? (
                        <MapPin className="w-4 h-4 text-gray-500" />
                      ) : (
                        <Building className="w-4 h-4 text-gray-500" />
                      )}
                      <span className="font-medium">
                        {locale.nombre}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      {locale.direccion}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {locale.tipo === 'PoS' ? 'Punto de Servicio' : 'Centro de Distribución'}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditLocale(locale)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => handleDeleteLocale(locale.id!)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 mb-6">
              No hay locales configurados
            </div>
          )}

          {showLocaleForm ? (
            <form onSubmit={handleSaveLocale} className="border rounded-md p-6">
              <h3 className="text-lg font-semibold mb-4">
                {editingLocaleId ? 'Editar Local' : 'Agregar Nuevo Local'}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="locale-name" className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre
                  </label>
                  <input
                    type="text"
                    id="locale-name"
                    value={newLocale.nombre}
                    onChange={(e) => setNewLocale(prev => ({ ...prev, nombre: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="locale-address" className="block text-sm font-medium text-gray-700 mb-1">
                    Dirección
                  </label>
                  <input
                    type="text"
                    id="locale-address"
                    value={newLocale.direccion}
                    onChange={(e) => setNewLocale(prev => ({ ...prev, direccion: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="locale-type" className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo
                  </label>
                  <select
                    id="locale-type"
                    value={newLocale.tipo}
                    onChange={(e) => setNewLocale(prev => ({ ...prev, tipo: e.target.value as 'PoS' | 'Centro de Distribución' }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <option value="PoS">Punto de Servicio</option>
                    <option value="Centro de Distribución">Centro de Distribución</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-4 mt-6">
                <button
                  type="submit"
                  disabled={savingLocale}
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center gap-2"
                >
                  {savingLocale ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4 text-blue-500" />
                  )}
                  {editingLocaleId ? 'Actualizar' : 'Guardar'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowLocaleForm(false);
                    setEditingLocaleId(null);
                    setNewLocale({
                      nombre: '',
                      direccion: '',
                      tipo: 'PoS'
                    });
                  }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          ) : (
            <button
              onClick={() => setShowLocaleForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Plus className="w-5 h-5" />
              Agregar Local
            </button>
          )}
        </div>
      </div>
    </div>
  );
};